import is from '@sindresorhus/is';
import { z } from 'zod';

import { type LanguageCodeForJSA, languageCodeSchemaForJSA } from '~/shared/internationalization';

import { JSA_PROFILE_TASK_DETAIL_TILE_TYPE } from './profile-task-tile-types';

export const PROFILE_TASK_TILE_WIDTH = '500px';

const parentTypeSchema = z.enum(['level', 'role']);
const opaqueIdSchema = z.string().trim().min(1);

const profileTaskTileBaseSchema = z.object({
    architectureId: z.uuid(),
    isCommentsOpen: z.boolean().optional(),
    language: languageCodeSchemaForJSA.optional(),
    parentId: z.uuid(),
    parentType: parentTypeSchema,
    parentVersion: z.number().int().positive().optional(),
    roleId: z.uuid().optional(),
    taskId: opaqueIdSchema,
    type: z.literal(JSA_PROFILE_TASK_DETAIL_TILE_TYPE),
});

export const persistedProfileTaskTileSchema = profileTaskTileBaseSchema.extend({
    draft: z.never().optional(),
    taskVersionId: opaqueIdSchema.optional(),
});

export const draftProfileTaskTileSchema = profileTaskTileBaseSchema.extend({
    draft: z.literal(true),
    taskVersionId: z.never().optional(),
});

export const profileTaskTileSchema = z.union([persistedProfileTaskTileSchema, draftProfileTaskTileSchema]);

export type PersistedProfileTaskTilePayload = z.infer<typeof persistedProfileTaskTileSchema>;
export type DraftProfileTaskTilePayload = z.infer<typeof draftProfileTaskTileSchema>;
export type ProfileTaskTilePayload = z.infer<typeof profileTaskTileSchema>;

type ProfileTaskTileBaseInput = {
    readonly architectureId: string;
    readonly isCommentsOpen?: boolean;
    readonly language?: LanguageCodeForJSA;
    readonly parentId: string;
    readonly parentType: 'level' | 'role';
    readonly parentVersion?: number;
    readonly roleId?: string;
    readonly taskId: string;
};

export const createPersistedProfileTaskTilePayload = ({
    architectureId,
    isCommentsOpen,
    language,
    parentId,
    parentType,
    parentVersion,
    roleId,
    taskId,
    taskVersionId,
}: ProfileTaskTileBaseInput & { readonly taskVersionId?: string }): PersistedProfileTaskTilePayload => ({
    architectureId,
    ...(!is.undefined(isCommentsOpen) ? { isCommentsOpen } : {}),
    ...(!is.undefined(language) ? { language } : {}),
    parentId,
    parentType,
    ...(!is.undefined(parentVersion) ? { parentVersion } : {}),
    ...(!is.undefined(roleId) ? { roleId } : {}),
    taskId,
    ...(!is.undefined(taskVersionId) ? { taskVersionId } : {}),
    type: JSA_PROFILE_TASK_DETAIL_TILE_TYPE,
});

export const createDraftProfileTaskTilePayload = ({
    architectureId,
    isCommentsOpen,
    language,
    parentId,
    parentType,
    parentVersion,
    roleId,
    taskId,
}: ProfileTaskTileBaseInput): DraftProfileTaskTilePayload => ({
    architectureId,
    draft: true,
    ...(!is.undefined(isCommentsOpen) ? { isCommentsOpen } : {}),
    ...(!is.undefined(language) ? { language } : {}),
    parentId,
    parentType,
    parentVersion,
    ...(!is.undefined(roleId) ? { roleId } : {}),
    taskId,
    type: JSA_PROFILE_TASK_DETAIL_TILE_TYPE,
});

export const isProfileTaskTilePayload = (payload: unknown): payload is ProfileTaskTilePayload =>
    is.plainObject(payload) && payload['type'] === JSA_PROFILE_TASK_DETAIL_TILE_TYPE;

const tokenValue = (tokens: readonly string[], key: string): string | undefined => {
    const index = tokens.indexOf(key);

    return index < 0 ? undefined : tokens[index + 1];
};

const TASK_TILE_OPTION_KEYS = new Set(['comments', 'lang', 'parentRole']);

export const parseProfileTaskTileTokens = (tokens: readonly string[]): unknown => {
    const parentVersionToken = tokens[7];
    const hasParentVersion =
        is.nonEmptyString(parentVersionToken) &&
        parentVersionToken.startsWith('v') &&
        /^\d+$/.test(parentVersionToken.slice(1));
    const taskIdIndex = hasParentVersion ? 8 : 7;
    const discriminator = tokens[taskIdIndex + 1];
    const hasDiscriminator = is.nonEmptyString(discriminator) && !TASK_TILE_OPTION_KEYS.has(discriminator);
    const shared = {
        architectureId: tokens[4],
        ...(tokenValue(tokens, 'comments') === 'open'
            ? { isCommentsOpen: true }
            : tokenValue(tokens, 'comments') === 'closed'
              ? { isCommentsOpen: false }
              : {}),
        language: tokenValue(tokens, 'lang'),
        parentId: tokens[6],
        parentType: tokens[5],
        ...(hasParentVersion ? { parentVersion: Number.parseInt(parentVersionToken.slice(1), 10) } : {}),
        roleId: tokenValue(tokens, 'parentRole'),
        taskId: tokens[taskIdIndex],
        type: tokens.slice(0, 4).join('.'),
    };

    if (discriminator === 'draft') {
        return { ...shared, draft: true };
    }

    return hasDiscriminator ? { ...shared, taskVersionId: discriminator } : shared;
};

export const serializeProfileTaskTilePayload = (payload: ProfileTaskTilePayload): string => {
    const versionToken = payload.draft === true ? 'draft' : payload.taskVersionId;
    const parentVersionSegment = is.number(payload.parentVersion) ? `.v${payload.parentVersion}` : '';
    const versionSegment = is.nonEmptyString(versionToken) ? `.${versionToken}` : '';
    const languageSuffix = payload.language === undefined ? '' : `.lang.${payload.language}`;
    const commentsSuffix =
        payload.isCommentsOpen === undefined ? '' : `.comments.${payload.isCommentsOpen ? 'open' : 'closed'}`;
    const roleSuffix = payload.roleId === undefined ? '' : `.parentRole.${payload.roleId}`;

    return `${payload.type}.${payload.architectureId}.${payload.parentType}.${payload.parentId}${parentVersionSegment}.${payload.taskId}${versionSegment}${languageSuffix}${commentsSuffix}${roleSuffix}`;
};

export const PROFILE_TASK_TILE_SLOT_KEY = 'jsa.profile-task';
